import { useEffect, useState } from 'react';
import { useMemoriesStore } from '../stores/memoriesStore';
import { useAuthStore } from '../stores/authStore';
import { EmptyState } from '../components/EmptyState';
import { Toast } from '../components/Toast';
import { DEFAULT_TAGS, formatDate, type Memory, type Tag } from '../types';

export function MemoriesPage() {
  const { memories, tags, isLoading, error, fetchMemories, fetchTags, createMemory, updateMemory, deleteMemory } = useMemoriesStore();
  const { token } = useAuthStore();
  const [showForm, setShowForm] = useState(false);
  const [editingMemory, setEditingMemory] = useState<Memory | null>(null);
  const [filterTag, setFilterTag] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    situation: '', decision: '', action: '', learning: '', tags: [] as Tag[], conversationId: null as string | null,
  });

  useEffect(() => {
    if (token) {
      fetchMemories({ tag: filterTag || undefined, query: searchQuery || undefined });
      fetchTags();
    }
  }, [token, filterTag, searchQuery, fetchMemories, fetchTags]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingMemory) {
      await updateMemory(editingMemory.id, formData);
    } else {
      await createMemory(formData);
    }
    setShowForm(false);
    setEditingMemory(null);
    resetForm();
  };

  const handleEdit = (memory: Memory) => {
    setEditingMemory(memory);
    setFormData({
      situation: memory.situation,
      decision: memory.decision,
      action: memory.action,
      learning: memory.learning,
      tags: memory.tags,
      conversationId: memory.conversationId,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Excluir esta memória?')) await deleteMemory(id);
  };

  const resetForm = () => setFormData({ situation: '', decision: '', action: '', learning: '', tags: [], conversationId: null });

  const toggleTag = (tag: Tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag],
    }));
  };

  if (!token) return <EmptyState title="Configure seu token" description="Vá em Configurações para definir seu token de acesso." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem' }}>
      {error && <Toast message={error} onClose={() => {}} />}

      <header style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Memórias Gerenciais</h2>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>+ Nova Memória</button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <input
            type="search"
            placeholder="Buscar memórias..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <select
            value={filterTag}
            onChange={e => setFilterTag(e.target.value)}
            style={{ minWidth: '160px' }}
          >
            <option value="">Todas as tags</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
            {DEFAULT_TAGS.filter(t => !tags.includes(t)).map(t => <option key={t} value={t}>{t} (padrão)</option>)}
          </select>
        </div>
      </header>

      {showForm && (
        <div className="modal-backdrop" onClick={() => { setShowForm(false); setEditingMemory(null); resetForm(); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ marginBottom: '1rem' }}>{editingMemory ? 'Editar Memória' : 'Nova Memória'}</h3>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Situação</label>
                <p style={{ fontSize: '0.75rem', color: 'var(--fg-muted)' }}>Preencha somente os fatos conhecidos. Deixe os demais campos vazios.</p>
                <textarea
                  value={formData.situation}
                  onChange={e => setFormData(prev => ({ ...prev, situation: e.target.value }))}
                  rows={3}
                  placeholder="O que aconteceu? Contexto do problema..."
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Decisão</label>
                <textarea
                  value={formData.decision}
                  onChange={e => setFormData(prev => ({ ...prev, decision: e.target.value }))}
                  rows={2}
                  placeholder="O que foi decidido?"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Ação</label>
                <textarea
                  value={formData.action}
                  onChange={e => setFormData(prev => ({ ...prev, action: e.target.value }))}
                  rows={2}
                  placeholder="Qual ação foi tomada ou será tomada?"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Aprendizado</label>
                <textarea
                  value={formData.learning}
                  onChange={e => setFormData(prev => ({ ...prev, learning: e.target.value }))}
                  rows={2}
                  placeholder="O que aprendeu? O que faria diferente?"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Tags</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {DEFAULT_TAGS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className={`badge ${formData.tags.includes(tag) ? 'active' : ''}`}
                      onClick={() => toggleTag(tag)}
                      style={{ background: formData.tags.includes(tag) ? 'var(--primary)' : 'var(--accent)' }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditingMemory(null); resetForm(); }}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={![formData.situation, formData.decision, formData.action, formData.learning].some(value => value.trim())}>
                  {editingMemory ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <div className="empty-state"><div className="spinner" style={{ width: '24px', height: '24px' }} /></div>
        ) : memories.length === 0 ? (
          <EmptyState title="Nenhuma memória" description="Crie sua primeira memória gerencial para começar a construir seu conhecimento." icon="🧠" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {memories.map(memory => (
              <div key={memory.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {memory.tags.map(tag => <span key={tag} className="badge">{tag}</span>)}
                </div>
                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                  <div><strong>Situação:</strong> {memory.situation || 'Não informado'}</div>
                  <div><strong>Decisão:</strong> {memory.decision || 'Não informado'}</div>
                  <div><strong>Ação:</strong> {memory.action || 'Não informado'}</div>
                  <div><strong>Aprendizado:</strong> {memory.learning || 'Não informado'}</div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--fg-muted)' }}>
                  <span>{formatDate(memory.createdAt)}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(memory)}>Editar</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleDelete(memory.id)}>Excluir</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 1rem; }
        .modal { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--radius); padding: 1.5rem; width: 100%; max-width: 500px; }
        .badge.active { background: var(--primary); color: white; }
        .spinner { border: 2px solid var(--border); border-top-color: var(--primary); border-radius: 50%; animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}